import React from 'react';
import { Target, LayoutDashboard, Users, Zap } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

interface ServiceCard {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  bullets: string[];
}

const services: ServiceCard[] = [
  {
    icon: Target,
    title: 'Sales Prospecting Tools',
    subtitle: 'Find better leads, faster.',
    bullets: [
      'Build a custom tool that finds companies matching your exact criteria',
      'Filter by industry, company size, geography, revenue, or any other signal',
      'Give your sales team a clean, prioritized list to work from, not a bloated database',
      'Replace the hours your team spends on manual research every week',
    ],
  },
  {
    icon: LayoutDashboard,
    title: 'Internal Business Apps',
    subtitle: 'Replace the spreadsheets that are breaking.',
    bullets: [
      'Custom dashboards, task trackers, approval flows, and reporting tools',
      "Built around how your team actually works, not around a generic template",
      'Reduce the manual handoffs that slow things down and cause mistakes',
      'Something your team opens every day because it actually makes their job easier',
    ],
  },
  {
    icon: Users,
    title: 'Client Portals',
    subtitle: 'A professional space for your clients.',
    bullets: [
      'Branded portal where clients see project status, submit documents, and send messages',
      'Replaces the email chains, shared folders, and "just checking in" conversations',
      'Clients feel in control, and you spend less time on updates',
      'Clean, simple, and built to match how you run your projects',
    ],
  },
  {
    icon: Zap,
    title: 'Workflow Automation',
    subtitle: "Stop doing things by hand that don't need a human.",
    bullets: [
      'Connect the tools you already use, like CRM, email, calendar, and ERP',
      'Automate notifications, follow ups, approvals, and reporting',
      'Processes that run in the background while your team focuses on real work',
      "We map the workflow first, then build exactly what's needed",
    ],
  },
];

export const DualOfferSection = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Subtle gradient divider */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-[rgba(255,255,255,0.1)] to-transparent" />

      <div className="container-professional" ref={elementRef} style={getRevealStyle()}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-primary font-mono mb-4">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            What we build
          </h2>
          <p className="text-[rgba(255,255,255,0.5)] max-w-xl mx-auto">
            We work across four areas. Most projects combine more than one.
          </p>
        </div>

        {/* 4-Column Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                className="group relative bg-[#0f0f0f] rounded-2xl border border-white/[0.07] p-7 hover:border-primary/30 transition-all duration-300 hover:-translate-y-0.5 flex flex-col h-full"
              >
                {/* Icon */}
                <div className="w-11 h-11 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-5 group-hover:border-primary/30 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-white mb-2 leading-snug tracking-[-0.015em]">
                  {service.title}
                </h3>

                {/* Subtitle */}
                <p className="text-sm text-white/55 mb-4 leading-relaxed">
                  {service.subtitle}
                </p>

                <div className="h-px w-8 bg-primary/30 mb-4" />

                {/* Bullets */}
                <ul className="space-y-2.5 mt-auto">
                  {service.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-[7px] w-1 h-1 rounded-full bg-primary/70 flex-shrink-0" />
                      <span className="text-[13px] text-white/60 leading-relaxed">
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
